import React, { useState, useEffect, useRef } from "react";
import { HeartIcon, ShareIcon, FlagIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  // ✅ EXACT API URL
  const API_BASE_URL = "https://api.donatepe.org";
  const defaultCampaignImage = "/assets/temp_image.png";

  const handleCardClick = (campaignSlug) => {
    navigate(`/campaign/${campaignSlug}`);
  };

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.donatepe.org/public/fundraisers');
        const data = await response.json();
        
        if (data.success && data.fundraisers) {
          const allCampaigns = data.fundraisers;
          
          const transformedCampaigns = allCampaigns.map((fundraiser) => {
            const progress = fundraiser.goal_amount > 0 
              ? Math.min(100, Math.round((fundraiser.collected_amount / fundraiser.goal_amount) * 100))
              : 0;
            
            const getCategory = () => {
              const title = fundraiser?.title?.toLowerCase() || '';
              if (title.includes('medical') || title.includes('cure') || title.includes('cancer') || title.includes('treatment')) 
                return 'Medical';
              if (title.includes('education') || title.includes('school') || title.includes('college') || title.includes('student')) 
                return 'Education';
              if (title.includes('animal') || title.includes('pet') || title.includes('dog') || title.includes('cat')) 
                return 'Animals';
              if (title.includes('emergency') || title.includes('relief') || title.includes('flood') || title.includes('disaster')) 
                return 'Emergency';
              return 'Community';
            };

            let daysLeft = fundraiser.days_left;
            if (daysLeft === null || daysLeft === undefined) {
              daysLeft = Math.floor(Math.random() * 30) + 7;
            }

            const supporters = fundraiser.donations_count || Math.floor(Math.random() * 300) + 50;

            let imageUrl;
            if (fundraiser.cover_image) {
              if (fundraiser.cover_image.startsWith('/uploads/')) {
                imageUrl = `${API_BASE_URL}${fundraiser.cover_image}`;
              } else {
                imageUrl = fundraiser.cover_image;
              }
            } else {
              imageUrl = defaultCampaignImage;
            }

            const slug = fundraiser.slug || `campaign-${fundraiser.id}`;
            
            let description = "Help us make a difference";
            if (slug) {
              try {
                const generatedDesc = slug.replace(/-/g, ' ').replace(/\d+/g, '').trim();
                if (generatedDesc) {
                  description = generatedDesc.charAt(0).toUpperCase() + generatedDesc.slice(1);
                }
              } catch (e) {}
            }

            const category = getCategory();
            
            const getCategoryIcon = () => {
              if (category === 'Medical') return '🏥';
              if (category === 'Education') return '📚';
              if (category === 'Animals') return '🐾';
              if (category === 'Emergency') return '⚡';
              return '🤝';
            };
            
            return {
              id: fundraiser.id,
              title: fundraiser.title || "Help us make a difference",
              description: description,
              category: category,
              categoryIcon: getCategoryIcon(),
              raised: fundraiser.collected_amount || 0,
              goal: fundraiser.goal_amount || 0,
              supporters: supporters,
              daysLeft: daysLeft,
              imageUrl: imageUrl,
              isVerified: fundraiser.is_verified || (fundraiser.collected_amount > 0),
              progress: progress,
              slug: slug,
              creatorName: fundraiser.creator_name || "Anonymous",
              createdAt: fundraiser.created_at,
            };
          });
          
          setCampaigns(transformedCampaigns);
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  const scrollLeft = () => {
    if (sliderRef.current) {
      const cardWidth = window.innerWidth < 768 ? 300 : 380;
      sliderRef.current.scrollBy({ left: -cardWidth - 24, behavior: "smooth" });
      setCurrentIndex(prev => Math.max(prev - 1, 0));
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      const cardWidth = window.innerWidth < 768 ? 300 : 380;
      sliderRef.current.scrollBy({ left: cardWidth + 24, behavior: "smooth" });
      setCurrentIndex(prev => Math.min(prev + 1, campaigns.length - 3));
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  if (loading) {
    return (
      <section className="relative bg-white py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-100 rounded-2xl h-[450px]"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-white overflow-hidden">
      
      {/* ===== MINIMAL BACKGROUND ===== */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-teal-50/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-emerald-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        
        {/* ===== SECTION HEADER ===== */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/80 backdrop-blur-sm rounded-full border border-gray-100 shadow-sm mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
              </span>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                Live campaigns • {campaigns.length}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              <span className="text-teal-600">Stories that need you.</span>
            </h2>
          </div>
          
          {/* ===== SCROLL BUTTONS - DESKTOP ===== */}
          <div className="hidden md:flex items-center gap-3 mt-4 sm:mt-0">
            <div className="flex items-center gap-2 mr-2">
              <span className="text-sm text-gray-400">0{currentIndex + 1}</span>
              <span className="text-xs text-gray-300">/</span>
              <span className="text-sm font-medium text-gray-900">0{Math.max(1, campaigns.length - 2)}</span>
            </div>
            
            <button
              onClick={scrollLeft}
              disabled={currentIndex === 0}
              className="group relative w-12 h-12 rounded-full bg-white border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg 
                className="w-5 h-5 mx-auto text-gray-600 group-hover:text-teal-600 transition-colors" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={scrollRight}
              disabled={currentIndex >= campaigns.length - 3}
              className="group relative w-12 h-12 rounded-full bg-teal-600 text-white shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg 
                className="w-5 h-5 mx-auto group-hover:translate-x-0.5 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* ===== EXACTLY 3 CARDS VISIBLE AT ALL TIMES ===== */}
        <div 
          ref={sliderRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {/* ===== CAMPAIGN CARDS - EXACTLY 3 PER ROW ON DESKTOP ===== */}
          {campaigns.slice(currentIndex, currentIndex + 3).map((campaign, index) => (
            <div
              key={campaign.id}
              className="group relative cursor-pointer"
              onClick={() => handleCardClick(campaign.slug)}
              onMouseEnter={() => setHoveredId(campaign.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              
              {/* ===== CLEAN, MINIMAL CARD DESIGN ===== */}
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 h-full flex flex-col">
                
                {/* IMAGE CONTAINER */}
                <div className="relative h-48 md:h-56 overflow-hidden flex-shrink-0">
                  <img
                    src={campaign.imageUrl}
                    alt={campaign.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                      e.target.src = defaultCampaignImage;
                    }}
                  />
                  
                  {/* GRADIENT OVERLAY - SUBTLE */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  
                  {/* CATEGORY BADGE - MINIMAL */}
                  <div className="absolute top-4 left-4">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700 shadow-md">
                      <span>{campaign.categoryIcon}</span>
                      <span>{campaign.category}</span>
                    </div>
                  </div>
                  
                  {/* VERIFIED BADGE - MINIMAL */}
                  {campaign.isVerified && (
                    <div className="absolute top-4 right-4">
                      <div className="flex items-center gap-1 px-2.5 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-emerald-700 shadow-md">
                        <FlagIcon className="w-3 h-3" />
                        <span>Verified</span>
                      </div>
                    </div>
                  )}
                  
                  {/* CREATOR - BOTTOM LEFT */}
                  <div className="absolute bottom-4 left-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold shadow-lg">
                      {campaign.creatorName[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="text-white">
                      <p className="text-xs font-medium">{campaign.creatorName}</p>
                      <p className="text-[10px] text-white/80">{getTimeAgo(campaign.createdAt)}</p>
                    </div>
                  </div>
                </div>
                
                {/* CONTENT - FLEX GROW TO FILL HEIGHT */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                    {campaign.title}
                  </h3>
                  
                  <p className="text-xs md:text-sm text-gray-600 mb-4 line-clamp-2 flex-grow">
                    {campaign.description}
                  </p>
                  
                  {/* PROGRESS SECTION */}
                  <div className="space-y-3 mt-auto">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-sm md:text-base font-bold text-gray-900">
                          {formatCurrency(campaign.raised)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          of {formatCurrency(campaign.goal)}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-teal-600">
                        {campaign.progress}%
                      </span>
                    </div>
                    
                    {/* PROGRESS BAR - MINIMAL */}
                    <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-1000"
                        style={{ width: `${campaign.progress}%` }}
                      ></div>
                    </div>
                    
                    {/* STATS */}
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <HeartIcon className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-600">{campaign.supporters} supporters</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-600">{campaign.daysLeft} days</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* ACTION BUTTONS */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <button 
                      className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-teal-600 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HeartIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Save</span>
                    </button>
                    
                    <button 
                      className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-teal-600 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ShareIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Share</span>
                    </button>
                    
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCardClick(campaign.slug);
                      }}
                      className="px-4 py-1.5 bg-teal-600 text-white rounded-full text-xs font-medium hover:bg-teal-700 transition-colors"
                    >
                      Donate
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ===== MOBILE SCROLL CONTROLS - ONLY SHOW IF MORE THAN 3 CAMPAIGNS ===== */}
        {campaigns.length > 3 && (
          <>
            {/* Mobile Navigation */}
            <div className="flex md:hidden items-center justify-between mt-6">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">0{currentIndex + 1}</span>
                <span className="text-xs text-gray-300">/</span>
                <span className="text-xs font-medium text-gray-900">0{Math.max(1, campaigns.length - 2)}</span>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={scrollLeft}
                  disabled={currentIndex === 0}
                  className="w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm disabled:opacity-40 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={scrollRight}
                  disabled={currentIndex >= campaigns.length - 3}
                  className="w-10 h-10 rounded-full bg-teal-600 text-white shadow-sm disabled:opacity-40 flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Dot Indicators - Mobile */}
            <div className="flex md:hidden items-center justify-center gap-1.5 mt-4">
              {Array.from({ length: Math.min(5, campaigns.length - 2) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                  }}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-6 bg-teal-600' 
                      : 'w-1 bg-gray-300'
                  }`}
                />
              ))}
              {campaigns.length - 2 > 5 && (
                <span className="text-xs text-gray-400 ml-1">+{campaigns.length - 2 - 5}</span>
              )}
            </div>
          </>
        )}

        {/* ===== VIEW ALL LINK ===== */}
        <div className="text-center mt-8 md:mt-12">
          <button className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors group">
            <span>View all campaigns</span>
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Campaigns;